import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HelpArticle } from '../help-center.types';

/**
 * Cuerpo de un artículo de ayuda (párrafos, listas, pasos, avisos, tablas y enlaces). Lo usan el Centro de
 * ayuda y el panel de ayuda contextual, para que el contenido se vea igual en los dos.
 */
@Component({
    selector: 'app-help-article',
    standalone: true,
    imports: [RouterLink, MatButtonModule, MatIconModule],
    template: `
        @for (block of article.blocks; track $index) {
            @switch (block.type) {
                @case ('p') {
                    <p class="mb-3 leading-relaxed" [innerHTML]="inline(block.text)"></p>
                }
                @case ('list') {
                    <ul class="mb-3 ml-5 list-disc space-y-1">
                        @for (item of block.items; track $index) {
                            <li [innerHTML]="inline(item)"></li>
                        }
                    </ul>
                }
                @case ('steps') {
                    <ol class="mb-3 ml-5 list-decimal space-y-1">
                        @for (item of block.items; track $index) {
                            <li [innerHTML]="inline(item)"></li>
                        }
                    </ol>
                }
                @case ('tip') {
                    <div class="mb-3 flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100">
                        <mat-icon class="icon-size-5 flex-shrink-0 text-blue-600">lightbulb</mat-icon>
                        <span [innerHTML]="inline(block.text)"></span>
                    </div>
                }
                @case ('warning') {
                    <div class="mb-3 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                        <mat-icon class="icon-size-5 flex-shrink-0 text-amber-600">warning</mat-icon>
                        <span [innerHTML]="inline(block.text)"></span>
                    </div>
                }
                @case ('table') {
                    <div class="mb-3 overflow-x-auto rounded-lg border">
                        <table class="w-full text-sm">
                            <thead class="bg-gray-50 text-left dark:bg-gray-900">
                                <tr>
                                    @for (header of block.headers; track $index) {
                                        <th class="px-3 py-2 font-semibold">{{ header }}</th>
                                    }
                                </tr>
                            </thead>
                            <tbody class="divide-y">
                                @for (row of block.rows; track $index) {
                                    <tr>
                                        @for (cell of row; track $index) {
                                            <td class="px-3 py-2 align-top" [innerHTML]="inline(cell)"></td>
                                        }
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                }
                @case ('links') {
                    <div class="mt-2 flex flex-wrap gap-2">
                        @for (link of block.links; track link.label) {
                            <a mat-stroked-button color="primary" [routerLink]="link.route" [queryParams]="link.queryParams" (click)="linkClicked.emit()">
                                <mat-icon class="icon-size-5">arrow_forward</mat-icon>
                                {{ link.label }}
                            </a>
                        }
                    </div>
                }
            }
        }
    `,
})
export class HelpArticleComponent {
    @Input({ required: true }) article!: HelpArticle;

    /** Se ha seguido un enlace del artículo a una pantalla (el panel contextual se cierra) */
    @Output() linkClicked = new EventEmitter<void>();

    /** Texto en línea con **negrita** y `código`, escapado */
    inline(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/`(.+?)`/g, '<code class="rounded bg-gray-100 px-1 py-0.5 text-sm dark:bg-gray-800">$1</code>');
    }
}
